import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Calendar, Clock, Check, SpinnerGap,
  CaretDown, Sparkle, CheckCircle, Shield, Warning,
  Desktop, VideoCamera, Phone, User, Buildings,
  EnvelopeSimple, CalendarCheck, ArrowLeft
} from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScheduleDemoPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    role: "",
    demo_type: [],
    preferred_date: "",
    preferred_time: "",
    timezone: "PST",
    meeting_type: "video",
    attendees: "1-2",
    questions: ""
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    checkCalendarStatus();
  }, []);

  const checkCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API}/calendar/status`);
      setCalendarConnected(response.data.connected);
    } catch (err) {
      console.log("Calendar status check failed:", err);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/calendar/available-slots`, {
        params: { date }
      });
      setAvailableSlots(response.data.slots || []);
      setCalendarConnected(response.data.calendar_connected);
    } catch (err) {
      console.log("Failed to fetch slots:", err);
      // Fallback to default slots
      setAvailableSlots([
        { time: "09:00", available: true },
        { time: "10:00", available: true },
        { time: "11:00", available: true },
        { time: "13:00", available: true },
        { time: "14:00", available: true },
        { time: "15:00", available: true },
        { time: "16:00", available: true }
      ]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (date) => {
    const dateStr = formatDateISO(date);
    setFormData({ ...formData, preferred_date: dateStr, preferred_time: "" });
    fetchAvailableSlots(dateStr);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const demoTypes = [
    { 
      id: "hospiceguard", 
      name: "HospiceGuard", 
      description: "Mobile hospice audit system demo",
      icon: Shield,
      color: "bg-emerald-500"
    },
    { 
      id: "incidentinsight", 
      name: "Incident Insight Pro", 
      description: "AI-powered incident reporting demo",
      icon: Warning,
      color: "bg-orange-500"
    },
    { 
      id: "both", 
      name: "Full Platform Demo", 
      description: "See both products in action",
      icon: Desktop,
      color: "bg-primary"
    }
  ];

  const timeSlots = [
    "9:00 AM", "10:00 AM", "11:00 AM",
    "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"
  ];

  const timezones = [
    { value: "PST", label: "Pacific (PST)" },
    { value: "MST", label: "Mountain (MST)" },
    { value: "CST", label: "Central (CST)" },
    { value: "EST", label: "Eastern (EST)" }
  ];

  const meetingTypes = [
    { value: "video", label: "Video Call (Zoom/Teams)", icon: VideoCamera },
    { value: "phone", label: "Phone Call", icon: Phone }
  ];

  const attendeeOptions = [
    { value: "1-2", label: "Just me or 1 other" },
    { value: "3-5", label: "Small team (3-5 people)" },
    { value: "6+", label: "Larger group (6+ people)" }
  ];

  // Generate next 14 business days
  const getAvailableDates = () => {
    const dates = [];
    let date = new Date();
    date.setDate(date.getDate() + 1); // Start from tomorrow
    
    while (dates.length < 14) {
      const day = date.getDay();
      if (day !== 0 && day !== 6) { // Skip weekends
        dates.push(new Date(date));
      }
      date.setDate(date.getDate() + 1);
    }
    return dates;
  };

  const availableDates = getAvailableDates();

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDateISO = (date) => {
    return date.toISOString().split('T')[0];
  };

  const handleDemoTypeToggle = (id) => {
    if (id === "both") {
      setFormData({ ...formData, demo_type: ["both"] });
    } else {
      let newTypes = [...formData.demo_type].filter(t => t !== "both");
      if (newTypes.includes(id)) {
        newTypes = newTypes.filter(t => t !== id);
      } else {
        newTypes.push(id);
      }
      setFormData({ ...formData, demo_type: newTypes });
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      const demoTypeLabel = formData.demo_type.includes("both") 
        ? "Full Platform Demo" 
        : formData.demo_type.map(t => 
            t === "hospiceguard" ? "HospiceGuard" : "Incident Insight Pro"
          ).join(" & ");

      // Submit to consultations endpoint
      await axios.post(`${API}/consultations`, {
        name: formData.name,
        email: formData.email,
        organization: formData.organization,
        phone: formData.phone,
        consultation_type: "product-demo",
        preferred_date: formData.preferred_date,
        preferred_time: `${formData.preferred_time} ${formData.timezone}`,
        notes: `Demo Request: ${demoTypeLabel}\nRole: ${formData.role}\nMeeting Type: ${formData.meeting_type}\nAttendees: ${formData.attendees}\n\nQuestions/Notes: ${formData.questions || "None provided"}`
      });

      // Also create calendar event if calendar is connected
      try {
        await axios.post(`${API}/calendar/book`, null, {
          params: {
            title: `Noble Healthcare Demo - ${demoTypeLabel}`,
            description: `Demo for ${formData.organization}\nContact: ${formData.name} (${formData.email})\nPhone: ${formData.phone || 'N/A'}\nRole: ${formData.role}\nMeeting Type: ${formData.meeting_type}\nAttendees: ${formData.attendees}\n\nNotes: ${formData.questions || 'None'}`,
            date: formData.preferred_date,
            time: formData.preferred_time,
            duration_minutes: 60,
            attendee_email: formData.email,
            attendee_name: formData.name
          }
        });
      } catch (calendarErr) {
        console.log("Calendar event creation skipped:", calendarErr);
      }
      
      setSuccess(true);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to schedule demo. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = formData.demo_type.length > 0;
  const canProceedStep2 = formData.preferred_date && formData.preferred_time;
  const canProceedStep3 = formData.name && formData.email && formData.organization;

  return (
    <div data-testid="schedule-demo-page">
      {/* Hero Section */}
      <section className="py-16 bg-hero-gradient relative overflow-hidden">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="decorative-blob w-72 h-72 bg-accent/20 bottom-0 left-0" />
        
        <div className="max-w-4xl mx-auto container-padding relative">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <Link 
              to="/products" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors"
            >
              <ArrowLeft weight="bold" className="w-4 h-4" />
              Back to Products
            </Link>
            <span className="badge badge-primary mb-6 mx-auto flex items-center justify-center w-fit">
              <CalendarCheck weight="fill" className="w-4 h-4 mr-2" />
              Schedule Your Demo
            </span>
            <h1 className="font-display text-4xl md:text-5xl text-foreground mb-4 leading-tight">
              See Our Products{" "}
              <span className="text-gradient">in Action</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Get a personalized walkthrough of HospiceGuard and Incident Insight Pro. 
              See how they can transform your compliance workflow.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Progress Steps */}
      {!success && (
        <section className="py-8 border-b border-border bg-white sticky top-20 z-40">
          <div className="max-w-4xl mx-auto container-padding">
            <div className="flex items-center justify-between">
              {[
                { num: 1, label: "Choose Demo" },
                { num: 2, label: "Select Time" },
                { num: 3, label: "Your Details" }
              ].map((s, index) => (
                <div key={s.num} className="flex items-center">
                  <div className={`flex items-center gap-3 ${step >= s.num ? "text-primary" : "text-muted-foreground"}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                      step > s.num 
                        ? "bg-primary text-white" 
                        : step === s.num 
                          ? "bg-primary/10 text-primary border-2 border-primary" 
                          : "bg-muted text-muted-foreground"
                    }`}>
                      {step > s.num ? <Check weight="bold" className="w-5 h-5" /> : s.num}
                    </div>
                    <span className="hidden sm:block font-medium">{s.label}</span>
                  </div>
                  {index < 2 && (
                    <div className={`w-12 sm:w-24 h-1 mx-4 rounded-full transition-all ${
                      step > s.num ? "bg-primary" : "bg-muted"
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto container-padding">
          {error && (
            <motion.div
              className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {error}
            </motion.div>
          )}

          {/* Step 1: Choose Demo Type */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="font-display text-2xl md:text-3xl text-foreground mb-2">
                What would you like to see?
              </h2>
              <p className="text-muted-foreground mb-8">
                Select one or more products for your demo session
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {demoTypes.map((demo) => (
                  <button
                    key={demo.id}
                    onClick={() => handleDemoTypeToggle(demo.id)}
                    className={`p-6 rounded-2xl border-2 text-left transition-all ${
                      formData.demo_type.includes(demo.id)
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid={`demo-type-${demo.id}`}
                  >
                    <div className={`w-12 h-12 rounded-xl ${demo.color} flex items-center justify-center mb-4`}>
                      <demo.icon weight="fill" className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-bold text-foreground mb-1">{demo.name}</h3>
                    <p className="text-sm text-muted-foreground">{demo.description}</p>
                    {formData.demo_type.includes(demo.id) && (
                      <div className="mt-4 flex items-center gap-2 text-primary text-sm font-semibold">
                        <CheckCircle weight="fill" className="w-5 h-5" />
                        Selected
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  disabled={!canProceedStep1}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  data-testid="step1-next"
                >
                  Continue
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Select Date & Time */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="font-display text-2xl md:text-3xl text-foreground mb-2">
                Pick a date and time
              </h2>
              <p className="text-muted-foreground mb-8">
                Choose when works best for you. All demos are approximately 30 minutes.
              </p>

              {/* Date Selection */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-foreground mb-3">Select a Date</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                  {availableDates.map((date) => (
                    <button
                      key={formatDateISO(date)}
                      onClick={() => handleDateSelect(date)}
                      className={`p-3 rounded-xl text-center transition-all ${
                        formData.preferred_date === formatDateISO(date)
                          ? "bg-primary text-white"
                          : "bg-muted hover:bg-primary/10"
                      }`}
                      data-testid={`date-${formatDateISO(date)}`}
                    >
                      <span className="block text-xs opacity-70">
                        {date.toLocaleDateString('en-US', { weekday: 'short' })}
                      </span>
                      <span className="block font-bold">
                        {date.getDate()}
                      </span>
                      <span className="block text-xs opacity-70">
                        {date.toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Selection */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-foreground mb-3">
                  Select a Time
                  {calendarConnected && (
                    <span className="ml-2 text-xs font-normal text-green-600">
                      (Live availability from calendar)
                    </span>
                  )}
                </label>
                {loadingSlots ? (
                  <div className="flex items-center justify-center py-8">
                    <SpinnerGap className="w-6 h-6 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground">Loading available times...</span>
                  </div>
                ) : availableSlots.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
                    {availableSlots.map((slot) => {
                      const hour = parseInt(slot.time.split(':')[0]);
                      const displayTime = hour >= 12 
                        ? `${hour === 12 ? 12 : hour - 12}:00 PM` 
                        : `${hour}:00 AM`;
                      return (
                        <button
                          key={slot.time}
                          onClick={() => slot.available && setFormData({ ...formData, preferred_time: displayTime })}
                          disabled={!slot.available}
                          className={`p-3 rounded-xl text-center font-medium transition-all ${
                            formData.preferred_time === displayTime
                              ? "bg-primary text-white"
                              : slot.available
                                ? "bg-muted hover:bg-primary/10"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed line-through"
                          }`}
                          data-testid={`time-${slot.time}`}
                        >
                          {displayTime}
                        </button>
                      );
                    })}
                  </div>
                ) : formData.preferred_date ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        onClick={() => setFormData({ ...formData, preferred_time: time })}
                        className={`p-3 rounded-xl text-center font-medium transition-all ${
                          formData.preferred_time === time
                            ? "bg-primary text-white"
                            : "bg-muted hover:bg-primary/10"
                        }`}
                        data-testid={`time-${time.replace(/\s/g, '-')}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    Please select a date first to see available times
                  </p>
                )}
              </div>

              {/* Timezone */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-foreground mb-2">Your Timezone</label>
                <div className="flex flex-wrap gap-2">
                  {timezones.map((tz) => (
                    <button
                      key={tz.value}
                      onClick={() => setFormData({ ...formData, timezone: tz.value })}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        formData.timezone === tz.value
                          ? "bg-primary text-white"
                          : "bg-muted hover:bg-primary/10"
                      }`}
                    >
                      {tz.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Meeting Type */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-foreground mb-3">How would you like to meet?</label>
                <div className="flex flex-wrap gap-3">
                  {meetingTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setFormData({ ...formData, meeting_type: type.value })}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                        formData.meeting_type === type.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <type.icon weight="duotone" className={`w-5 h-5 ${
                        formData.meeting_type === type.value ? "text-primary" : "text-muted-foreground"
                      }`} />
                      <span className="font-medium">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="btn-secondary flex items-center gap-2"
                >
                  <ArrowLeft weight="bold" className="w-5 h-5" />
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!canProceedStep2}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  data-testid="step2-next"
                >
                  Continue
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Contact Details */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="font-display text-2xl md:text-3xl text-foreground mb-2">
                Almost there! Tell us about yourself
              </h2>
              <p className="text-muted-foreground mb-8">
                We'll use this information to prepare a personalized demo for you
              </p>

              <div className="bg-white border border-border rounded-2xl p-6 md:p-8 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">
                      <User weight="bold" className="w-4 h-4 inline mr-2" />
                      Your Name *
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="demo-form-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">
                      <EnvelopeSimple weight="bold" className="w-4 h-4 inline mr-2" />
                      Work Email *
                    </label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="john@hospice.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="demo-form-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">
                      <Buildings weight="bold" className="w-4 h-4 inline mr-2" />
                      Organization *
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Your Hospice Name"
                      value={formData.organization}
                      onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      required
                      data-testid="demo-form-org"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">
                      <Phone weight="bold" className="w-4 h-4 inline mr-2" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      className="form-input"
                      placeholder="(555) 555-5555"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="demo-form-phone"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-foreground mb-2">Your Role</label>
                  <div className="relative">
                    <select
                      className="form-select pr-10"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      data-testid="demo-form-role"
                    >
                      <option value="">Select your role...</option>
                      <option value="administrator">Administrator / Executive Director</option>
                      <option value="don">Director of Nursing / Clinical Director</option>
                      <option value="compliance">Compliance Officer / Quality Manager</option>
                      <option value="it">IT / Operations</option>
                      <option value="other">Other</option>
                    </select>
                    <CaretDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-foreground mb-2">How many will attend the demo?</label>
                  <div className="flex flex-wrap gap-2">
                    {attendeeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setFormData({ ...formData, attendees: option.value })}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          formData.attendees === option.value
                            ? "bg-primary text-white"
                            : "bg-muted hover:bg-primary/10"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Any specific questions or areas you'd like us to cover?
                  </label>
                  <textarea
                    className="form-textarea"
                    placeholder="Tell us about your current challenges or what you're most interested in seeing..."
                    value={formData.questions}
                    onChange={(e) => setFormData({ ...formData, questions: e.target.value })}
                    data-testid="demo-form-questions"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 mb-8">
                <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <CalendarCheck weight="fill" className="w-5 h-5 text-primary" />
                  Demo Summary
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Product(s):</span>
                    <p className="font-semibold text-foreground">
                      {formData.demo_type.includes("both") 
                        ? "Full Platform Demo"
                        : formData.demo_type.map(t => 
                            t === "hospiceguard" ? "HospiceGuard" : "Incident Insight Pro"
                          ).join(", ")}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Date:</span>
                    <p className="font-semibold text-foreground">
                      {formData.preferred_date 
                        ? new Date(formData.preferred_date + 'T12:00:00').toLocaleDateString('en-US', { 
                            weekday: 'long',
                            month: 'long', 
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : "Not selected"}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Time:</span>
                    <p className="font-semibold text-foreground">
                      {formData.preferred_time} {formData.timezone}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="btn-secondary flex items-center gap-2"
                >
                  <ArrowLeft weight="bold" className="w-5 h-5" />
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!canProceedStep3 || loading}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  data-testid="schedule-demo-submit"
                >
                  {loading ? (
                    <>
                      <SpinnerGap className="w-5 h-5 animate-spin" />
                      Scheduling...
                    </>
                  ) : (
                    <>
                      <CalendarCheck weight="fill" className="w-5 h-5" />
                      Schedule Demo
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Success */}
          {step === 4 && success && (
            <motion.div
              className="text-center py-12"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle weight="fill" className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-display text-3xl md:text-4xl text-foreground mb-4">
                You're All Set!
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
                Your demo has been scheduled for{" "}
                <span className="font-semibold text-foreground">
                  {new Date(formData.preferred_date + 'T12:00:00').toLocaleDateString('en-US', { 
                    weekday: 'long',
                    month: 'long', 
                    day: 'numeric'
                  })}
                </span>{" "}
                at{" "}
                <span className="font-semibold text-foreground">
                  {formData.preferred_time} {formData.timezone}
                </span>.
              </p>
              <p className="text-muted-foreground mb-8">
                You'll receive a confirmation email at <span className="font-semibold">{formData.email}</span> with 
                meeting details and a calendar invite.
              </p>
              
              <div className="bg-primary/5 rounded-2xl p-6 max-w-md mx-auto mb-8">
                <Sparkle weight="fill" className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-bold text-foreground mb-2">What's Next?</h3>
                <ul className="text-sm text-muted-foreground space-y-2 text-left">
                  <li className="flex items-start gap-2">
                    <Check weight="bold" className="w-4 h-4 text-primary mt-0.5" />
                    Check your email for the calendar invite
                  </li>
                  <li className="flex items-start gap-2">
                    <Check weight="bold" className="w-4 h-4 text-primary mt-0.5" />
                    Our team will prepare a personalized demo
                  </li>
                  <li className="flex items-start gap-2">
                    <Check weight="bold" className="w-4 h-4 text-primary mt-0.5" />
                    Feel free to invite colleagues to join
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/products"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  Explore Products
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </Link>
                <Link
                  to="/resources"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  Browse Resources
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ScheduleDemoPage;
